import React from 'react';

import { useParams } from 'next/navigation';
import queryString from 'query-string';

import { ChevronLeft, ChevronRight } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import clean from '@utils/cleanObject';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { useAppRouter } from '@contexts/router';
import { scrollToTop } from '@components/ScrollToTop/hooks';

import {
  CountTitle,
  NavigationButton,
  NavigationButtonTitle,
  NavigationContainer,
  PageButton,
  PageNumbersContainer,
  PaginationWrapper,
  SearchTitle,
  TextContainer,
} from './styled';
import { generatePageRange, getPrefix } from './utils';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  siblingCount?: number;
  onPageChange?: (page: number) => void;
  searchTitle?: string;
  type:
    | 'hotel'
    | 'tour'
    | 'bus'
    | 'domesticFlight'
    | 'abroadFlight'
    | 'carRental';
  totalItemCount: number;
}

const getTypeToName = {
  hotel: 'Otel',
  tour: 'Tur',
  bus: 'Otobüs',
  domesticFlight: 'Uçak',
  abroadFlight: 'Uçak',
  carRental: 'Araç',
};

const Pagination = ({
  currentPage,
  totalPages,
  siblingCount = 2,
  onPageChange,
  searchTitle,
  type = 'hotel',
  totalItemCount,
}: PaginationProps) => {
  const pageRange = generatePageRange(siblingCount, currentPage, totalPages);
  const query = useSearchParamsToQuery();
  const params = useParams();
  const { push } = useAppRouter();

  const getPageLink = (page: number) => {
    const { ...nextQuery } = query;

    if (page === 1) {
      delete nextQuery.page;
    }

    const queryParamsToHref = queryString.stringify(
      clean(page === 1 ? { ...nextQuery } : { ...nextQuery, page: page })
    );
    const prefix = getPrefix(type);

    return `${prefix}${params?.slug ? `/${params.slug}` : ''}${
      queryParamsToHref ? `?${queryParamsToHref}` : ''
    }`;
  };

  const handleNavigation = (page: number) => {
    onPageChange?.(page);
    const url = getPageLink(page);
    push(url);
    scrollToTop();
  };

  return (
    <PaginationWrapper>
      {searchTitle && totalItemCount && type && totalPages ? (
        <TextContainer>
          <SearchTitle> {searchTitle}</SearchTitle> aramanızda
          <CountTitle> {totalItemCount}</CountTitle> {getTypeToName[type]},
          <CountTitle> {totalPages}</CountTitle> sayfada listelenmektedir.
        </TextContainer>
      ) : null}
      <NavigationContainer>
        <NavigationButton
          disabled={currentPage < 2}
          onClick={() => {
            if (currentPage < 2) return null;
            handleNavigation(currentPage - 1);
            scrollToTop();
          }}
        >
          <CustomIcon
            icon={ChevronLeft}
            paletteColor={currentPage < 2 ? 'iron' : 'eclipse'}
            fontSize={14}
          />
          <NavigationButtonTitle disabled={currentPage < 2}>
            Önceki
          </NavigationButtonTitle>
        </NavigationButton>

        <PageNumbersContainer>
          {pageRange?.map((page) => (
            <PageButton
              prefetch={false}
              key={page}
              $isActive={page === currentPage}
              href={getPageLink(page)}
              onClick={async () => {
                handleNavigation(page);
                scrollToTop();
              }}
            >
              {page}
            </PageButton>
          ))}
        </PageNumbersContainer>
        <NavigationButton
          disabled={currentPage >= totalPages}
          onClick={() => {
            if (currentPage >= totalPages) return null;
            handleNavigation(currentPage + 1);
            scrollToTop();
          }}
        >
          <NavigationButtonTitle
            disabled={currentPage === totalPages || currentPage > totalPages}
          >
            Sonraki
          </NavigationButtonTitle>
          <CustomIcon
            icon={ChevronRight}
            paletteColor={
              currentPage === totalPages || currentPage > totalPages
                ? 'iron'
                : 'eclipse'
            }
            fontSize={14}
          />
        </NavigationButton>
      </NavigationContainer>
    </PaginationWrapper>
  );
};

export default Pagination;
